import { json, type ActionFunctionArgs, type MetaFunction } from '@remix-run/cloudflare';

import type { LinksFunction } from '@remix-run/node';
import stylesheet from '../tailwind.css?url';

import { Carousel } from '../components/carousel/carousel';
import { TestimonialSection } from '../sections/testimonials';
import { BenefitsSection } from '../sections/benefits';
import { MastersSection } from '../sections/Masters';
import { SarkarSection } from '../sections/Sarkar';
import { FAQSection } from '../sections/faq';
import { FooterSection } from '../sections/footer';
import { getPrefersReducedMotion } from '../accessibility/motion';
import { VYUKHeader } from '../components/header/vy-uk-header';
import { Seeker } from '../components/get-in-touch/get-in-touch';
import { isRetryableNetworkError, retry } from '../network/retry';
import { useLocation } from '@remix-run/react';
import clsx from 'clsx';

export const links: LinksFunction = () => [{ rel: 'stylesheet', href: stylesheet }];

export const meta: MetaFunction = () => {
  return [
    { title: 'Vihangam Yog UK' },
    {
      name: 'description',
      content: 'The United Kingdom institute of Vihangam Yog',
    },
  ];
};

const SCROLL_OFFSET = 60;

export const goToSelector = (selector: string) => {
  const element = document.getElementById(selector);
  if (!element) return;
  const prefersReducedMotion = getPrefersReducedMotion();

  window.scrollTo({
    behavior: prefersReducedMotion ? 'instant' : 'smooth',
    top: element.getBoundingClientRect().top - SCROLL_OFFSET,
  });
};

export const nav = [
  {
    title: 'The Masters',
    link: '/#Masters-section',
    isExternalLink: true,
  },
  {
    title: 'Faq',
    link: '/#faq-section',
    isExternalLink: true,
  },
  {
    title: 'Past Events',
    link: '/past-events',
    isExternalLink: true,
  },
];

export const action = async ({ request }: ActionFunctionArgs) => {
  try {
    const data = await request.formData();

    const name = data.get('name')?.toString();
    const interests = data.get('interests')?.toString();
    const email = data.get('email')?.toString();

    if (!name || !interests || !email) {
      return json('Not found', 404);
    }

    const interestsArr = JSON.parse(interests) as string[];

    const inquiryObj = {
      name,
      interests: interestsArr,
      email,
    } satisfies Seeker;

    try {
      const response = await retry(
        'post-inquiry',
        () =>
          fetch('https://api.vihangamyog.uk/api/inquiry', {
            method: 'POST',
            body: JSON.stringify(inquiryObj),
            headers: {
              'Content-Type': 'application/json',
            },
          }),
        {
          shouldRetryOnSuccess: isRetryableNetworkError,
        },
      );

      if (response.ok) {
        return json('OK', 200);
      }

      return Error('Bad request: ' + (await response.text()));
    } catch (e) {
      console.error(e);
      return json('Bad request', 500);
    }
  } catch (e) {
    return json('Not found', 404);
  }
};

const newEvents = [
  {
    city: 'London',
    name: 'HCS',
    date: '15 July 2025',
    time: '2:30 pm – 4:30 pm',
    hasBooking: false,
    source: 'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-london-15th.jpeg',
    id: 'london',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: '3 Lyndhurst Avenue, London N12 0LX, United Kingdom',
  },
  {
    city: 'Hopton',
    name: '12 Pebbleview Walk',
    date: '18 July 2025',
    time: '6:30 pm – 8:00 pm',
    hasBooking: false,
    source: 'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-hopton-18th.jpeg',
    id: 'hopton',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: 'Hopton NR31 9SG',
  },
  {
    city: 'Norwich',
    name: 'University of East Anglia',
    date: '18 July 2025',
    time: '2:30 pm – 5:00 pm',
    hasBooking: false,
    source: 'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-norwich-18th.jpeg',
    id: 'norwich',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: 'Norwich NR4 7TJ, United Kingdom',
  },
  {
    city: 'Bradford',
    name: 'Lakshmi Narayan Temple',
    date: '19 July 2025',
    time: '10:00 am - 12:00 pm',
    hasBooking: false,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-bradford-19th.jpeg',
    id: 'bradford',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: 'Bradford BD3 9LS, United Kingdom',
  },
  {
    city: 'Manchester',
    name: 'Raj Kaushal Gita Bhavan',
    date: '19 July 2025',
    time: '4:00 pm – 7:00 pm',
    hasBooking: false,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-manchester-19th.jpeg',
    id: 'manchester',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: '231 Withington Road, Manchester M16 8LU, United Kingdom',
  },
  {
    city: 'Leeds',
    name: 'Leeds Hindu Mandir',
    date: '20 July 2025',
    time: '10:30 am - 12:30 pm',
    hasBooking: false,
    source: 'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-leeds-20th.jpeg',
    id: 'leeds',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: '36 Alexandra Rd, Burley, Leeds LS6 1RF, United Kingdom',
  },
  {
    city: 'Leicester',
    name: 'Shivalaya - The Leicestershire Brahma Samaj',
    date: '20 July 2025',
    time: '5:00 pm - 7:00 pm',
    hasBooking: false,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/vy-uk-leicester-20th.jpeg',
    id: 'leicester',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
    location: '15, Belgrave Rd, Leicester LE4, United Kingdom',
  },
] as const;

const NewEventsSection = () => {
  const location = useLocation();
  const currentEventSelection = location.hash ? location.hash.replace('#', '') : '';

  return (
    <section
      id="new-events-section"
      className="w-full mt-16 py-8 bg-blue-50 items-center flex flex-col text-center px-6 md:px-36 lg:px-60"
    >
      <h2 className="text-center font-display font-bold text-xl">
        Upcoming Events - July 2025
      </h2>
      <p className="mt-1 text-center text-gray-600">
        Join us for meditation sessions and spiritual discourses across the UK.
      </p>
      <div className="grid grid-cols-1 w-full sm:grid-cols-2 items-start gap-4 gap-y-2 whitespace-nowrap mt-4">
        {newEvents.map((e) => (
          <div
            key={e.id}
            id={e.id}
            className={clsx(
              'mt-3 bg-white w-full rounded-md p-4 flex flex-col relative items-center sm:items-start gap-3 flex-wrap justify-center xs:justify-normal',
              {
                'border-[3px] border-blue-400': currentEventSelection === e.id,
                'border-[3px] border-transparent': currentEventSelection !== e.id,
              },
            )}
          >
            <div className="flex flex-col items-center sm:items-start">
              <p className="text-lg font-bold leading-snug">
                {[e.name, e.city].filter((s) => s).join(', ')}
              </p>
              <p className="text-gray-500">
                {e.time} • {e.date}
              </p>
              {e.location && (
                <p className="text-sm text-gray-400 mt-1 text-center sm:text-left">
                  {e.location}
                </p>
              )}
              <div className="flex flex-row flex-wrap justify-center sm:justify-normal items-center gap-2 mt-2">
                {e.tags.map((t) => (
                  <span
                    key={t}
                    className="bg-gray-100 uppercase text-[10px] px-2 py-1 rounded-sm tracking-wider"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-row items-start gap-3 flex-wrap w-full">
              <a
                href={e.source}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-[unset]"
              >
                <button className="w-full text-center sm:max-w-[unset] px-3 sm:px-5 py-3 sm:uppercase sm:tracking-widest text-sm sm:text-xs bg-blue-100 hover:bg-blue-200 active:bg-blue-50 rounded-md">
                  View Details
                </button>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Index() {
  return (
    <div className="w-full h-full relative">
      <VYUKHeader />
      <main className="xs:mt-12 mb-16 sm:mt-24 w-full">
        <Carousel />
        <p className="text-2xl px-3 sm:text-4xl text-center max-w-[95%] font-display font-black italic m-auto mt-8 xs:mt-15">
          The most powerful, ancient meditation
          <br />
          technique, rediscovered for the modern world.
        </p>
        <NewEventsSection />
        <TestimonialSection />
        <BenefitsSection />
        <MastersSection />
        <SarkarSection />
        {/* <HavanCTA /> */}
        <FAQSection />
      </main>
      <FooterSection />
    </div>
  );
}
